import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, ArrowUp } from 'lucide-react';
import { TableOfContents } from './content/TableOfContents';
import { CompassRose } from './CompassRose';
import { TodayButton } from './TodayButton';
import { TravelButton } from './TravelButton';
import { DirectionModal } from './DirectionModal';
import { ReviewButton } from './ReviewButton';
import { Direction, getDailyInfo } from '../data/directionsData';
import { ContentPage } from './ContentPage';
import { RatingSection } from './rating/RatingSection';

export const ContentSection = () => {
  const [selectedDirection, setSelectedDirection] = useState<Direction>();
  const [showTodayModal, setShowTodayModal] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const readingTime = '15 मिनट';
  const publishDate = '2024-03-14';
  const lastUpdated = '2024-03-14';

  const handleTodayClick = () => {
    const today = new Date();
    const dayName = new Intl.DateTimeFormat('hi-IN', { weekday: 'long' }).format(today);
    const dailyInfo = getDailyInfo(dayName);
    
    if (dailyInfo) {
      const direction = {
        name: dailyInfo.prohibitedDirection,
        description: dailyInfo.description,
        remedies: dailyInfo.remedies,
        special: dailyInfo.special
      } as Direction;
      
      setSelectedDirection(direction);
      setShowTodayModal(true);
    }
  };

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 filter drop-shadow-lg">
            दिशा शूल
          </h1>
          <p className="text-xl text-purple-100">यात्रा के लिए शुभ दिशा जानें</p>
        </header>

        <div className="relative max-w-4xl mx-auto mb-16">
          <CompassRose onDirectionClick={setSelectedDirection} />
        </div>

        <div className="mt-8 flex flex-col items-center space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-center">
            <TodayButton onClick={handleTodayClick} />
            <TravelButton />
          </div>
          <ReviewButton />
        </div>

        <div className="max-w-4xl mx-auto mt-8">
          <div className="flex items-center justify-between mb-6 bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-sm">
            <div className="flex flex-col gap-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Clock size={20} />
                <span>पढ़ने का समय: {readingTime}</span>
              </div>
              <div className="text-xs">
                <div>प्रकाशित: {publishDate}</div>
                <div>अपडेट: {lastUpdated}</div>
              </div>
            </div>
          </div>

          <article className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
            <TableOfContents />
            <ContentPage />
            <RatingSection />
          </article>
        </div>
      </div>

      <DirectionModal 
        direction={selectedDirection} 
        onClose={() => {
          setSelectedDirection(undefined);
          setShowTodayModal(false);
        }}
        isToday={showTodayModal}
      />

      {showScrollTop && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-purple-600 text-white p-3 rounded-full shadow-lg hover:bg-purple-700 transition-colors z-50"
          aria-label="Scroll to top"
        >
          <ArrowUp size={24} />
        </motion.button>
      )}
    </>
  );
};